import { DefaultJustification, DefaultFontWidth, LineAlignment } from 'DataLibrary/Dms/MultiMsgConstants';
import { Font } from 'NSwag/ServerClient/Client.nswag';
import { reportError } from 'Common/Errors/ErrorReporter';

interface LineArgs {
    onChange: (update: ILineUpdate) => void;
    maxWidth: number;
    font?: Font | null;
    index: number;
}

export interface ILineUpdate {
    lineIndex: number;
    overflow?: string | null;
    underflow?: number | null;
    dequeuedChars?: string | null;
    setHardLineBreak?: boolean;
}

/**
 * An object representation of the DMS editor line.
 */
export class Line {
    text: string;
    alignment: LineAlignment;
    onChange: (update: ILineUpdate) => void;
    maxWidth: number;
    font?: Font | null;
    index: number;

    constructor({ onChange, maxWidth, font, index }: LineArgs) {
        this.text = '';
        this.alignment = LineAlignment.Center;
        this.onChange = onChange;
        this.maxWidth = maxWidth;
        this.index = index;
        this.font = font;
    }

    /**
     * Used to parse the initial multi message and return any overflow.
     * @param value - the multi string for this line.
     */
    parseLineMulti = (value: string): string => {
        const tag = value.match(/\[jl([0-9])]/i);

        if (tag) {
            this.alignment = parseInt(tag[1]) as LineAlignment;
            value = value.replace(/\[jl[0-9]]/i, '');
        }

        // Remove [fnt] tag as it is unused.
        value = value.replace('[fnt]', '');

        const unhandledTags = value.match(/\[.*]/g);
        if (unhandledTags) {
            // This should never happen if the areTagsSupported check is doing its job
            console.log('Unsupported tags not filtered correctly.', `Unsupported multi - ${value}`, unhandledTags);
            reportError('Unsupported tags not filtered correctly.', `Unsupported multi - ${value}`);
            value = value.replace(/\[.*]/g, '');
        }

        return this.updateText(value + '\r');
    }

    /**
     * Called to add new characters to the end of an existing line that can
     * fit the extra chars.
     * @param chars - The new char(s) to add to the end of this line
     * @param updatePhase - Is this the being called during an update?
     */
    appendChars = (chars: string, updatePhase = false): string => {
        // If this is already a hard line break and we are
        // adding another return, just return as overflow.
        // This handles the case when a user focuses a line
        // with a hard line break and hits enter again.
        const isHardLineBreak = this.text.endsWith('\r');
        const overflow = isHardLineBreak && chars === '\r'
            ? chars
            : this.updateText(this.text.replace('\r', '') + chars);

        if (updatePhase) {
            this.onChange({
                lineIndex: this.index,
                overflow: overflow,
                setHardLineBreak: !isHardLineBreak && chars === '\r'
            });
        }

        return overflow;
    }

    /**
     * Used to add new characters to the beginning of a new line.
     * @param chars - The new char(s) to add to the beginning of this line
     */
    prependChars = (chars: string): string => {
        return this.updateText(chars + this.text);
    }

    /**
     * Used to remove characters from this line to be added to a previous line/phase
     * @param numberOfChars - the number of characters to remove from the beginning of this line.
     */
    dequeueChars(numberOfChars: number, updatePhase = false): string {
        // TODO: Handle the case where we have a hard line break left or empty line
        const dequeuedChars = this.text.substring(0, numberOfChars);
        this.text = this.text.substring(dequeuedChars.length);

        if (updatePhase) {
            this.onChange({
                lineIndex: this.index,
                underflow: dequeuedChars.length,
                dequeuedChars
            });
        }

        return dequeuedChars;
    }

    /**
     * Used to calculate the size of a new line.
     */
    getStringWidth = (value: string): number => {
        let width: number;

        const font = this.font;
        // if (font && font.characters) {
        //     width = value.split('').reduce((w, char) => {
        //         let c = font!.characters!.find(c => c.char == char);
        //         if (c) {
        //             w += c.width
        //         }
        //         return w;
        //     }, 0);
        // } else {
            width = value.length * DefaultFontWidth;
        // }

        return width;
    }

    getSliceIndex = (value: string): number => {
        let sliceIndex = -1;
        let width = 0;

        while (width < this.maxWidth) {
            // width += this.font
            //     ? this.font!.characters!.find(c => c.char === value[sliceIndex + 1])!.width
            //     : DefaultFontWidth;

            // Workaround until fonts are properly supported.
            width += DefaultFontWidth;
            sliceIndex++;
        }

        return sliceIndex;
    }

    /**
     * Called to update the current backing line for the DMS editor line input.
     * @param event - The event from an input onChange handler.
     */
    update = (value: string): string => {
        const update: ILineUpdate = {
            lineIndex: this.index
        };

        const isHardLineBreak = this.text.endsWith('\r');
        update.setHardLineBreak = !isHardLineBreak && value.endsWith('\r');

        // If line is already a hard line break, maintain that break
        if (isHardLineBreak) {
            value += '\r';
        }

        if (value.length < this.text.length && !isHardLineBreak) {
            update.underflow = this.text.length - value.length;
        }
        update.overflow = this.updateText(value);

        // Update phase
        this.onChange(update);

        return update.overflow;
    }

    updateText = (text: string): string => {
        let overflow = '';
        this.text = text;

        if (this.text.includes('\r') && this.text.length !== 1) {
            const index = this.text.indexOf('\r');
            const parts = [this.text.substring(0, index), this.text.substring(index + 1)];
            const width = this.getStringWidth(parts[0]);
            if (width <= this.maxWidth) {
                this.text = `${parts[0]}\r`;
                overflow = parts[1];
            }
        }

        const width = this.getStringWidth(this.text);
        if (width > this.maxWidth) {
            const sliceIndex = this.getSliceIndex(this.text);
            overflow = this.text.substring(sliceIndex) + overflow;
            this.text = this.text.substring(0, sliceIndex);
        }

        return overflow;
    }

    toString = (): string => {
        return this.text.replace('\r', '');
    }

    toMultiText = (): string => {
        return `[jl${this.alignment}]${this.text.replace('\r', '')}`;
    }
}
